import { ArrowRight, Mail } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  EnfinityPrimaryBtn,
  EnfinitySecondaryBtn,
} from "@/components/design-system/enfinity/EnfinityButton";
import scrollTo from "@/utils/scrollTo";

const CTA_BG_URL = "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/cta-bg.png";

export default function CTA({
  heading,
  subtitle,
  primaryButtonText,
  primaryButtonLink,
  secondaryButtonText,
  secondaryButtonLink,
  badgeText
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;

  const handlePrimaryButtonClick = () => {
    // If on main page, scroll to FAQ
    if (path === '/' || path === '/enfinity' || path === '/enfinity/main') {
      scrollTo("faqs");
    } else {
      // Navigate to main page, then scroll to FAQ
      navigate("/");
      setTimeout(() => {
        const scrollToSection = (attempts = 0) => {
          if (attempts > 15) return;
          const section = document.getElementById("faqs");
          if (section) {
            section.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          } else {
            setTimeout(() => scrollToSection(attempts + 1), 100);
          }
        };
        scrollToSection();
      }, 200);
    }
  };

  const handleSecondaryButtonClick = () => {
    // If secondary button is for FAQ, navigate to FAQ section
    if (finalSecondaryButtonLink === "/enfinity#faqs" || finalSecondaryButtonLink === "/#faqs" || finalSecondaryButtonLink?.includes("#faqs")) {
      // If on main page, scroll to FAQ
      if (path === '/' || path === '/enfinity' || path === '/enfinity/main') {
        scrollTo("faqs");
      } else {
        // Navigate to main page, then scroll to FAQ
        navigate("/");
        setTimeout(() => {
          const scrollToSection = (attempts = 0) => {
            if (attempts > 15) return;
            const section = document.getElementById("faqs");
            if (section) {
              section.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            } else {
              setTimeout(() => scrollToSection(attempts + 1), 100);
            }
          };
          scrollToSection();
        }, 200);
      }
    }
  };

  // Get default content based on route
  const getDefaultContent = () => {
    // Check for enfinipay route or pay landing page
    if (path.includes('/our-solutions/enfinipay') || (path === '/' && import.meta.env.VITE_LANDING_PAGE === 'pay')) {
      return {
        heading: "Ready to take control of your revenue?",
        subtitle: "Join thousands of creators and turn your earnings into instant payouts with EnfiniPay",
        primaryButtonText: "Get paid faster with Pay",
        primaryButtonLink: "https://core.enfinity.com/signup",
        secondaryButtonText: "Message us",
        secondaryButtonLink: "/our-solutions/enfinipay/contact-us",
        badgeText: "YouTube Certified Partner • Microsoft Certified Partner"
      };
    } else if (path.includes('/our-solutions/enfinishield') || (path === '/' && import.meta.env.VITE_LANDING_PAGE === 'shield')) {
      return {
        heading: "Protect your content from unauthorized use",
        subtitle: "Join thousands of creators protecting their content with EnfiniShield",
        primaryButtonText: "Get protected with EnfiniShield",
        primaryButtonLink: "/our-solutions/enfinishield/esign",
        secondaryButtonText: "Message us",
        secondaryButtonLink: "/our-solutions/enfinishield/contact-us",
        badgeText: "YouTube Certified Rights Management Partner"
      };
    } else if (path.includes('/our-solutions/enfiniboost') || (path === '/' && import.meta.env.VITE_LANDING_PAGE === 'boost')) {
      return {
        heading: "Ready to earn more with MSN?",
        subtitle: "Join thousands of creators already getting more views with EnfiniBoost",
        primaryButtonText: "Reach MSN with Boost",
        primaryButtonLink: "/our-solutions/enfiniboost/esign",
        secondaryButtonText: "Message us",
        secondaryButtonLink: "/our-solutions/enfiniboost/contact-us",
        badgeText: "YouTube Certified Partner • Microsoft Certified Partner"
      };
    } else if (path.includes('/contact-us')) {
      return {
        heading: "Ready to unlock your channel's full potential?",
        subtitle: "Join 6,000+ creators who trust Enfinity with their revenue, content protection, and growth.",
        primaryButtonText: "Create an account",
        primaryButtonLink: "https://core.enfinity.com/signup",
        secondaryButtonText: "Browse our FAQ",
        secondaryButtonLink: "/enfinity#faqs",
        badgeText: "YouTube Certified Partner • Microsoft Certified Partner"
      };
    } else if (path.includes('/about-us')) {
      return {
        heading: "Ready to unlock your channel's full potential?",
        subtitle: "Join 6,000+ creators who trust Enfinity with their revenue, content protection, and growth.",
        primaryButtonText: "Create an account",
        primaryButtonLink: "https://core.enfinity.com/signup",
        secondaryButtonText: "Message us",
        secondaryButtonLink: "/contact-us",
        badgeText: "YouTube Certified Partner • Microsoft Certified Partner"
      };
    } else if (path.includes('/case-studies')) {
      return {
        heading: "Ready to unlock your channel's full potential?",
        subtitle: "Join 6,000+ creators who trust Enfinity with their revenue, content protection, and growth.",
        primaryButtonText: "Create an account",
        primaryButtonLink: "https://core.enfinity.com/signup",
        secondaryButtonText: "Message us",
        secondaryButtonLink: "/contact-us",
        badgeText: "YouTube Certified Partner • Microsoft Certified Partner"
      };
    } else {
      // Default: Main page
      return {
        heading: "Ready to unlock your channel's full potential?",
        subtitle: "Join 6,000+ creators who trust Enfinity with their revenue, content protection, and growth.",
        primaryButtonText: "Create an acount",
        primaryButtonLink: "https://core.enfinity.com/signup",
        secondaryButtonText: "Message us",
        secondaryButtonLink: "/contact-us",
        badgeText: "YouTube Certified Partner • Microsoft Certified Partner"
      };
    }
  };

  const defaultContent = getDefaultContent();

  const finalHeading = heading || defaultContent.heading;
  const finalSubtitle = subtitle || defaultContent.subtitle;
  const finalPrimaryButtonText = primaryButtonText || defaultContent.primaryButtonText;
  const finalPrimaryButtonLink = primaryButtonLink || defaultContent.primaryButtonLink;
  const finalSecondaryButtonText = secondaryButtonText || defaultContent.secondaryButtonText;
  const finalSecondaryButtonLink = secondaryButtonLink || defaultContent.secondaryButtonLink;
  const finalBadgeText = badgeText || defaultContent.badgeText;

  // Get button color based on route
  const getButtonStyle = () => {
    const landingPage = import.meta.env.VITE_LANDING_PAGE;
    if (path.includes('/our-solutions/enfinipay') || (path === '/' && landingPage === 'pay')) {
      return { background: "linear-gradient(to right, #00A656, #127E00)" };
    } else if (path.includes('/our-solutions/enfinishield') || (path === '/' && landingPage === 'shield')) {
      return { background: "#8B5CF6" };
    } else if (path.includes('/our-solutions/enfiniboost') || (path === '/' && landingPage === 'boost')) {
      return { background: "linear-gradient(to right, #60A5FA, #3B82F6)" };
    } else if (finalPrimaryButtonText === "Get paid faster with Pay" || defaultContent.primaryButtonText === "Get paid faster with Pay") {
      return { background: "linear-gradient(to right, #00A656, #127E00)" };
    } else {
      // Default: use new primary button color
      return { background: "#292929" };
    }
  };
  
  return (
    <div
      className="relative py-20 lg:py-32 px-6 xxs:px-8 xs:px-12 sm:px-12 md:px-12 bg-[#1a1a1a] bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${CTA_BG_URL})` }}
    >
      <div className="flex flex-col items-center justify-center gap-6 max-w-5xl mx-auto">
        {/* Main Heading */}
        <h1 className="text-5xl xxs:text-[55px] lg:text-[80px] 4xl:text-[90px] font-normal text-white text-center leading-tight xxs:leading-tight xs:leading-tight sm:leading-normal md:leading-normal lg:leading-normal max-w-full enfinity-header-font">
        {finalHeading}
        </h1>
        
        {/* Subtitle */}
        <p className="text-lg xxs:text-[1rem] lg:text-[22px] font-normal text-white text-center max-w-[1076px] leading-[30px] enfinity-subtext-font" style={{ lineHeight: "1.2" }}>
        {finalSubtitle}
        </p>

        {/* Buttons */}
        <div className="flex flex-col md:flex-row items-center justify-center gap-[10px] h-auto md:h-[60px] w-full md:w-auto">
          {finalSecondaryButtonLink?.includes("#faqs") || finalSecondaryButtonLink === "/enfinity#faqs" ? (
            <EnfinitySecondaryBtn 
              className="w-full md:w-auto"
              onClick={handleSecondaryButtonClick}
            >
              <span>{finalSecondaryButtonText}</span>
              <Mail className="w-6 h-6" />
            </EnfinitySecondaryBtn>
          ) : finalSecondaryButtonLink?.startsWith('http') ? (
            <a
              href={finalSecondaryButtonLink}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full md:w-auto"
            >
              <EnfinitySecondaryBtn className="w-full md:w-auto">
                <span>{finalSecondaryButtonText}</span>
                <Mail className="w-6 h-6" />
              </EnfinitySecondaryBtn>
            </a>
          ) : (
            <Link to={finalSecondaryButtonLink} className="w-full md:w-auto">
              <EnfinitySecondaryBtn className="w-full md:w-auto">
                <span>{finalSecondaryButtonText}</span>
                <Mail className="w-6 h-6" />
              </EnfinitySecondaryBtn>
            </Link>
          )}
          
          {finalPrimaryButtonLink?.startsWith('http') ? (
            <a
              href={finalPrimaryButtonLink}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full md:w-auto"
            >
              <EnfinityPrimaryBtn
                className="w-full md:w-auto"
                style={getButtonStyle()}
                noBorder={path.includes('/our-solutions/enfiniboost') || path.includes('/our-solutions/enfinishield') || path.includes('/our-solutions/enfinipay')}
              >
                <span>{finalPrimaryButtonText}</span>
                <ArrowRight className="w-4 h-4" />
              </EnfinityPrimaryBtn>
            </a>
          ) : finalPrimaryButtonLink === "/enfinity#faqs" || finalPrimaryButtonLink === "/#faqs" || finalPrimaryButtonLink?.includes("#faqs") ? (
            <EnfinityPrimaryBtn
              onClick={handlePrimaryButtonClick}
              className="w-full md:w-auto"
              style={getButtonStyle()}
              noBorder={path.includes('/our-solutions/enfiniboost') || path.includes('/our-solutions/enfinishield') || path.includes('/our-solutions/enfinipay')}
            >
              <span>{finalPrimaryButtonText}</span>
              <ArrowRight className="w-4 h-4" />
            </EnfinityPrimaryBtn>
          ) : (
            <Link to={finalPrimaryButtonLink} className="w-full md:w-auto">
              <EnfinityPrimaryBtn
                className="w-full md:w-auto"
                style={getButtonStyle()}
                noBorder={path.includes('/our-solutions/enfiniboost') || path.includes('/our-solutions/enfinishield') || path.includes('/our-solutions/enfinipay')}
              >
                <span>{finalPrimaryButtonText}</span>
                <ArrowRight className="w-4 h-4" />
              </EnfinityPrimaryBtn>
            </Link>
          )}
        </div>

        {/* Badge Text */}
        <p className={`text-base xxs:text-[1rem] lg:text-[18px] font-normal text-white text-center enfinity-subtext-font ${path.includes("/about-us") || path.includes("/case-studies") || path.includes("/contact-us") || path.includes("/our-solutions/enfinipay/contact-us") || path.includes("/our-solutions/enfiniboost/contact-us") || path.includes("/our-solutions/enfinishield/contact-us") || path.includes("/our-solutions/enfinipay/about-us") || path.includes("/our-solutions/enfiniboost/about-us") || path.includes("/our-solutions/enfinishield/about-us") || path.includes("/our-solutions/enfinipay/case-studies") || path.includes("/our-solutions/enfiniboost/case-studies") || path.includes("/our-solutions/enfinishield/case-studies") ? "pb-36" : ""}`}>
        {finalBadgeText}
        </p>
      </div>
    </div>
  );
}
